import React, { useEffect, useState } from 'react';
import { XIcon, SaveIcon, PlusIcon, TrashIcon, MailIcon, PhoneIcon, MapPinIcon, CheckIcon } from 'lucide-react';
// Types
export type EmailType = 'Work' | 'Personal' | 'Other';
export type PhoneType = 'Mobile' | 'Work' | 'Home' | 'Fax';
export type AddressType = 'Billing' | 'Shipping' | 'Physical' | 'Mailing';
export interface ContactEmail {
  id: string;
  type: EmailType;
  value: string;
  isPrimary: boolean;
}
export interface ContactPhone {
  id: string;
  type: PhoneType;
  value: string;
  isPrimary: boolean;
}
export interface ContactAddress {
  id: string;
  type: AddressType;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isPrimary: boolean;
}
export interface ContactData {
  id: string;
  name: string;
  role: string;
  emails: ContactEmail[];
  phones: ContactPhone[];
  addresses: ContactAddress[];
  // Legacy fields for backward compatibility
  email?: string;
  phone?: string;
  isPrimary?: boolean; // Contact-level primary flag
}
interface ContactFormProps {
  contact?: ContactData;
  onSave: (contactData: ContactData) => void;
  onCancel: () => void;
  locale: string;
}
export function ContactForm({
  contact,
  onSave,
  onCancel,
  locale
}: ContactFormProps) {
  // Initialize state with migration logic for legacy data
  const [formData, setFormData] = useState<ContactData>(() => {
    const baseData = {
      id: contact?.id || '',
      name: contact?.name || '',
      role: contact?.role || '',
      isPrimary: contact?.isPrimary || false,
      emails: contact?.emails || [],
      phones: contact?.phones || [],
      addresses: contact?.addresses || []
    };
    // Migrate legacy email if no emails array exists
    if (baseData.emails.length === 0 && contact?.email) {
      baseData.emails.push({
        id: `email-${Date.now()}`,
        type: 'Work',
        value: contact.email,
        isPrimary: true
      });
    } else if (baseData.emails.length === 0) {
      // Add empty initial email
      baseData.emails.push({
        id: `email-${Date.now()}`,
        type: 'Work',
        value: '',
        isPrimary: true
      });
    }
    // Migrate legacy phone if no phones array exists
    if (baseData.phones.length === 0 && contact?.phone) {
      baseData.phones.push({
        id: `phone-${Date.now()}`,
        type: 'Work',
        value: contact.phone,
        isPrimary: true
      });
    }
    return baseData;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'details' | 'addresses'>('details');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  // --- Email Handlers ---
  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev.emails, {
        id: `email-${Date.now()}`,
        type: 'Work',
        value: '',
        isPrimary: prev.emails.length === 0
      }]
    }));
  };
  const removeEmail = (id: string) => {
    setFormData(prev => {
      const newEmails = prev.emails.filter(e => e.id !== id);
      // If we removed the primary email, make the first one primary
      if (newEmails.length > 0 && !newEmails.some(e => e.isPrimary)) {
        newEmails[0].isPrimary = true;
      }
      return {
        ...prev,
        emails: newEmails
      };
    });
  };
  const updateEmail = (id: string, field: keyof ContactEmail, value: any) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map(email => {
        if (email.id !== id) return email;
        return {
          ...email,
          [field]: value
        };
      })
    }));
  };
  const setPrimaryEmail = (id: string) => {
    setFormData(prev => ({
      ...prev,
      emails: prev.emails.map(email => ({
        ...email,
        isPrimary: email.id === id
      }))
    }));
  };
  // --- Phone Handlers ---
  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev.phones, {
        id: `phone-${Date.now()}`,
        type: 'Mobile',
        value: '',
        isPrimary: prev.phones.length === 0
      }]
    }));
  };
  const removePhone = (id: string) => {
    setFormData(prev => {
      const newPhones = prev.phones.filter(p => p.id !== id);
      if (newPhones.length > 0 && !newPhones.some(p => p.isPrimary)) {
        newPhones[0].isPrimary = true;
      }
      return {
        ...prev,
        phones: newPhones
      };
    });
  };
  const updatePhone = (id: string, field: keyof ContactPhone, value: any) => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.map(phone => {
        if (phone.id !== id) return phone;
        return {
          ...phone,
          [field]: value
        };
      })
    }));
  };
  const setPrimaryPhone = (id: string) => {
    setFormData(prev => ({
      ...prev,
      phones: prev.phones.map(phone => ({
        ...phone,
        isPrimary: phone.id === id
      }))
    }));
  };
  // --- Address Handlers ---
  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, {
        id: `addr-${Date.now()}`,
        type: 'Physical',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        isPrimary: prev.addresses.length === 0
      }]
    }));
    setActiveTab('addresses');
  };
  const removeAddress = (id: string) => {
    setFormData(prev => {
      const newAddresses = prev.addresses.filter(a => a.id !== id);
      if (newAddresses.length > 0 && !newAddresses.some(a => a.isPrimary)) {
        newAddresses[0].isPrimary = true;
      }
      return {
        ...prev,
        addresses: newAddresses
      };
    });
  };
  const updateAddress = (id: string, field: keyof ContactAddress, value: any) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr => {
        if (addr.id !== id) return addr;
        return {
          ...addr,
          [field]: value
        };
      })
    }));
  };
  const setPrimaryAddress = (id: string) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map(addr => ({
        ...addr,
        isPrimary: addr.id === id
      }))
    }));
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    // Validate emails
    if (formData.emails.length === 0) {
      newErrors.emails = 'At least one email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hasValidEmail = formData.emails.some(e => e.value.trim() && emailRegex.test(e.value));
      if (!hasValidEmail) {
        newErrors.emails = 'Please provide a valid email address';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Ensure legacy fields are populated for backward compatibility
      const primaryEmail = formData.emails.find(e => e.isPrimary) || formData.emails[0];
      const primaryPhone = formData.phones.find(p => p.isPrimary) || formData.phones[0];
      const submissionData = {
        ...formData,
        email: primaryEmail?.value || '',
        phone: primaryPhone?.value || ''
      };
      onSave(submissionData);
    }
  };
  return <div className="bg-white flex flex-col h-[90vh] sm:h-auto sm:max-h-[90vh]" data-id="element-7766">
      <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-7767">
        <div className="flex items-center justify-between" data-id="element-7768">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-7769">
            {contact ? 'Edit Contact' : 'Add New Contact'}
          </h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-500" data-id="element-7770">
            <XIcon className="h-5 w-5" data-id="element-7771" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mt-4 border-b border-gray-100" data-id="element-7772">
          <button type="button" onClick={() => setActiveTab('details')} className={`pb-2 text-sm font-medium ${activeTab === 'details' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7773">
            Contact Details
          </button>
          <button type="button" onClick={() => setActiveTab('addresses')} className={`pb-2 text-sm font-medium ${activeTab === 'addresses' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7774">
            Addresses ({formData.addresses.length})
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-7775">
        <form id="contact-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-6" data-id="element-7776">
          {activeTab === 'details' ? <>
              {/* Basic Info */}
              <div className="space-y-4" data-id="element-7777">
                <div data-id="element-7778">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-7779">
                    Name <span className="text-red-500" data-id="element-7780">*</span>
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${errors.name ? 'border-red-300' : ''}`} placeholder="Enter contact name" data-id="element-7781" />
                  {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-7782">{errors.name}</p>}
                </div>

                <div data-id="element-7783">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700" data-id="element-7784">
                    Role / Title
                  </label>
                  <input type="text" id="role" name="role" value={formData.role} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="e.g., Site Manager, Finance Director" data-id="element-7785" />
                </div>

                <div className="flex items-center" data-id="element-7786">
                  <input type="checkbox" id="isPrimary" name="isPrimary" checked={formData.isPrimary} onChange={handleChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-7787" />
                  <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-900" data-id="element-7788">
                    Set as primary contact for organization
                  </label>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6" data-id="element-7789"></div>

              {/* Emails Section */}
              <div className="space-y-3" data-id="element-7790">
                <div className="flex items-center justify-between" data-id="element-7791">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-7792">
                    Email Addresses <span className="text-red-500" data-id="element-7793">*</span>
                  </label>
                  <button type="button" onClick={addEmail} className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800" data-id="element-7794">
                    <PlusIcon className="h-3 w-3 mr-1" data-id="element-7795" />
                    Add Email
                  </button>
                </div>

                {formData.emails.map((email, index) => <div key={email.id} className="flex items-start gap-2 bg-gray-50 p-3 rounded-md" data-id="element-7796">
                    <div className="mt-2" data-id="element-7797">
                      <MailIcon className="h-4 w-4 text-gray-400" data-id="element-7798" />
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2" data-id="element-7799">
                      <div className="sm:col-span-3" data-id="element-7800">
                        <select value={email.type} onChange={e => updateEmail(email.id, 'type', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" data-id="element-7801">
                          <option value="Work" data-id="element-7802">Work</option>
                          <option value="Personal" data-id="element-7803">Personal</option>
                          <option value="Other" data-id="element-7804">Other</option>
                        </select>
                      </div>
                      <div className="sm:col-span-9" data-id="element-7805">
                        <input type="email" value={email.value} onChange={e => updateEmail(email.id, 'value', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" placeholder="email@example.com" data-id="element-7806" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1" data-id="element-7807">
                      <button type="button" onClick={() => setPrimaryEmail(email.id)} className={`p-1 rounded-full ${email.isPrimary ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`} title={email.isPrimary ? 'Primary Email' : 'Set as Primary'} data-id="element-7808">
                        <CheckIcon className="h-4 w-4" data-id="element-7809" />
                      </button>
                      {formData.emails.length > 1 && <button type="button" onClick={() => removeEmail(email.id)} className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50" data-id="element-7810">
                          <TrashIcon className="h-4 w-4" data-id="element-7811" />
                        </button>}
                    </div>
                  </div>)}
                {errors.emails && <p className="text-sm text-red-600" data-id="element-7812">{errors.emails}</p>}
              </div>

              {/* Phones Section */}
              <div className="space-y-3 pt-2" data-id="element-7813">
                <div className="flex items-center justify-between" data-id="element-7814">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-7815">
                    Phone Numbers
                  </label>
                  <button type="button" onClick={addPhone} className="inline-flex items-center text-xs font-medium text-indigo-600 hover:text-indigo-800" data-id="element-7816">
                    <PlusIcon className="h-3 w-3 mr-1" data-id="element-7817" />
                    Add Phone
                  </button>
                </div>

                {formData.phones.length === 0 && <div className="text-sm text-gray-500 italic bg-gray-50 p-3 rounded-md text-center" data-id="element-7818">
                    No phone numbers added.
                  </div>}

                {formData.phones.map(phone => <div key={phone.id} className="flex items-start gap-2 bg-gray-50 p-3 rounded-md" data-id="element-7819">
                    <div className="mt-2" data-id="element-7820">
                      <PhoneIcon className="h-4 w-4 text-gray-400" data-id="element-7821" />
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2" data-id="element-7822">
                      <div className="sm:col-span-3" data-id="element-7823">
                        <select value={phone.type} onChange={e => updatePhone(phone.id, 'type', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" data-id="element-7824">
                          <option value="Mobile" data-id="element-7825">Mobile</option>
                          <option value="Work" data-id="element-7826">Work</option>
                          <option value="Home" data-id="element-7827">Home</option>
                          <option value="Fax" data-id="element-7828">Fax</option>
                        </select>
                      </div>
                      <div className="sm:col-span-9" data-id="element-7829">
                        <input type="tel" value={phone.value} onChange={e => updatePhone(phone.id, 'value', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-xs py-1.5" placeholder="+1 (555) 123-4567" data-id="element-7830" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-1" data-id="element-7831">
                      <button type="button" onClick={() => setPrimaryPhone(phone.id)} className={`p-1 rounded-full ${phone.isPrimary ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`} title={phone.isPrimary ? 'Primary Phone' : 'Set as Primary'} data-id="element-7832">
                        <CheckIcon className="h-4 w-4" data-id="element-7833" />
                      </button>
                      <button type="button" onClick={() => removePhone(phone.id)} className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50" data-id="element-7834">
                        <TrashIcon className="h-4 w-4" data-id="element-7835" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </> /* Addresses Tab */ : <div className="space-y-4" data-id="element-7836">
              <div className="flex items-center justify-between mb-2" data-id="element-7837">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-7838">
                  Address List
                </h4>
                <button type="button" onClick={addAddress} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7839">
                  <PlusIcon className="h-3 w-3 mr-1" data-id="element-7840" />
                  Add Address
                </button>
              </div>

              {formData.addresses.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200" data-id="element-7841">
                  <MapPinIcon className="mx-auto h-8 w-8 text-gray-400" data-id="element-7842" />
                  <p className="mt-2 text-sm text-gray-500" data-id="element-7843">
                    No addresses added yet
                  </p>
                  <button type="button" onClick={addAddress} className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-7844">
                    Add one now
                  </button>
                </div> : <div className="space-y-4" data-id="element-7845">
                  {formData.addresses.map(addr => <div key={addr.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 relative" data-id="element-7846">
                      <div className="absolute top-2 right-2 flex items-center gap-1" data-id="element-7847">
                        <button type="button" onClick={() => setPrimaryAddress(addr.id)} className={`p-1 rounded-full ${addr.isPrimary ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`} title={addr.isPrimary ? 'Primary Address' : 'Set as Primary'} data-id="element-7848">
                          <CheckIcon className="h-4 w-4" data-id="element-7849" />
                        </button>
                        <button type="button" onClick={() => removeAddress(addr.id)} className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50" data-id="element-7850">
                          <TrashIcon className="h-4 w-4" data-id="element-7851" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 gap-y-3 gap-x-4 sm:grid-cols-6" data-id="element-7852">
                        <div className="sm:col-span-2" data-id="element-7853">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-7854">
                            Type
                          </label>
                          <select value={addr.type} onChange={e => updateAddress(addr.id, 'type', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-7855">
                            <option value="Physical" data-id="element-7856">Physical</option>
                            <option value="Mailing" data-id="element-7857">Mailing</option>
                            <option value="Billing" data-id="element-7858">Billing</option>
                            <option value="Shipping" data-id="element-7859">Shipping</option>
                          </select>
                        </div>

                        <div className="sm:col-span-4" data-id="element-7860">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-7861">
                            Street Address
                          </label>
                          <input type="text" value={addr.street} onChange={e => updateAddress(addr.id, 'street', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="123 Main St" data-id="element-7862" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-7863">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-7864">
                            City
                          </label>
                          <input type="text" value={addr.city} onChange={e => updateAddress(addr.id, 'city', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-7865" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-7866">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-7867">
                            State/Province
                          </label>
                          <input type="text" value={addr.state} onChange={e => updateAddress(addr.id, 'state', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-7868" />
                        </div>

                        <div className="sm:col-span-2" data-id="element-7869">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-7870">
                            Postal Code
                          </label>
                          <input type="text" value={addr.postalCode} onChange={e => updateAddress(addr.id, 'postalCode', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-7871" />
                        </div>

                        <div className="sm:col-span-3" data-id="element-7872">
                          <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-7873">
                            Country
                          </label>
                          <input type="text" value={addr.country} onChange={e => updateAddress(addr.id, 'country', e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" data-id="element-7874" />
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>}
        </form>
      </div>

      <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0" data-id="element-7875">
        <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7876">
          Cancel
        </button>
        <button type="submit" form="contact-form" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7877">
          <SaveIcon className="h-4 w-4 mr-2" data-id="element-7878" />
          {contact ? 'Update Contact' : 'Add Contact'}
        </button>
      </div>
    </div>;
}